"use client";

import { useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLenis } from "@/hooks/useLenis";
import { useHeroSlider } from "@/hooks/useHeroSlider";
import { collections } from "@/data/collections";
import { HeroBackground } from "./HeroBackground";
import { HeroContent } from "./HeroContent";
import { HeroCards } from "./HeroCards";
import { SliderControls } from "./SliderControls";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

// Cursor is pure decoration — it isn't part of the visible content and
// has nothing to do with LCP. Splitting it into its own chunk (and never
// rendering it on the server) keeps it out of the JS the browser has to
// parse/execute before the hero itself can paint and become interactive.
const Cursor = dynamic(
  () => import("@/components/layout/Cursor").then((m) => m.Cursor),
  { ssr: false }
);

export function Hero() {
  // Smooth scroll
  useLenis();

  const { activeIndex, prevIndex, active, goTo, next, prev, duration } =
    useHeroSlider(collections, 0);

  const heroRef = useRef<HTMLElement>(null);
  const bgWrapRef = useRef<HTMLDivElement>(null);
  const contentColRef = useRef<HTMLDivElement>(null);
  const cardsColRef = useRef<HTMLDivElement>(null);
  const liveRegionRef = useRef<HTMLParagraphElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      )
        return;

      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [next, prev]);

  // Parallax scroll — deferred one frame so it never competes with the
  // hero's first paint; ScrollTrigger measurement work only starts once
  // the browser has already shown the user something.
  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (reduced || !heroRef.current) return;

    const id = requestAnimationFrame(() => {
      const ctx = gsap.context(() => {
        const scrollOpts = {
          trigger: heroRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        };

        if (bgWrapRef.current) {
          gsap.to(bgWrapRef.current, {
            y: 80,
            ease: "none",
            scrollTrigger: scrollOpts,
          });
        }
        if (contentColRef.current) {
          gsap.to(contentColRef.current, {
            y: -50,
            ease: "none",
            scrollTrigger: scrollOpts,
          });
        }
        if (cardsColRef.current) {
          gsap.to(cardsColRef.current, {
            y: -25,
            ease: "none",
            scrollTrigger: scrollOpts,
          });
        }
      }, heroRef);

      return () => ctx.revert();
    });

    return () => cancelAnimationFrame(id);
  }, []);

  // Announce slide to screen readers
  useEffect(() => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = `Now showing ${active.category}, collection ${active.collectionNumber}`;
    }
  }, [active]);

  // Touch handling
  function handleTouchStart(e: React.TouchEvent) {
    const t = e.touches[0];
    if (!t) return;
    touchStartX.current = t.clientX;
    touchStartY.current = t.clientY;
  }

  function handleTouchEnd(e: React.TouchEvent) {
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - touchStartX.current;
    const dy = t.clientY - touchStartY.current;

    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 48) {
      goTo(activeIndex + (dx < 0 ? 1 : -1));
    }
  }

  return (
    <section
      ref={heroRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-roledescription="carousel"
      aria-label="Featured collections"
      className="relative flex min-h-svh w-full flex-col overflow-hidden"
    >
      {/* Background layers */}
      <div ref={bgWrapRef} className="absolute inset-0">
        <HeroBackground
          collections={collections}
          activeIndex={activeIndex}
          prevIndex={prevIndex}
          duration={duration}
        />
      </div>

      {/* Ambient cursor — loaded after hydration, off the critical path */}
      <Cursor containerRef={heroRef} />

      {/* Main content */}
      <div className="relative z-[6] flex flex-1 flex-wrap-reverse items-end lg:items-center gap-[clamp(24px,4vw,56px)] px-5 pb-16 pt-[140px] sm:px-9 sm:pb-24 sm:pt-[110px] lg:px-14">
        {/* Text content */}
        <div ref={contentColRef} className="flex-[1_1_380px] min-w-[280px]">
          <HeroContent
            active={active}
            activeIndex={activeIndex}
            duration={duration}
          />
        </div>

        {/* Cards + controls */}
        <div
          ref={cardsColRef}
          className="flex min-w-[260px] max-w-[560px] flex-[1_1_320px] flex-col items-center gap-6 lg:items-end"
        >
          <HeroCards
            collections={collections}
            activeIndex={activeIndex}
            onSelect={(i) => goTo(i)}
          />
          <SliderControls
            counterValue={active.collectionNumber}
            onPrev={prev}
            onNext={next}
          />
        </div>
      </div>

      {/* Screen reader live region */}
      <p
        ref={liveRegionRef}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
      />
    </section>
  );
}